import { BaseRepository } from './BaseRepository';
import { UserState } from '../store/reducers/authentication';
import { User } from '../models';
import EventBus from '../services/EventBus';
import { createFormData } from '../utils/createFormData';
import handleResponseError from './utils/handleResponseError';
import store from '../store';
import { logout } from '../store/actions/authentication';

//Change authentication params to just token?
export interface AuthenticationParams {
  email?: string;
  password?: string;
  name?: string;
  firstName?: string;
  accessToken?: string;
}

export interface RegistrationParams extends AuthenticationParams {
  birthName: string;
  gender: string;
  birthdate: string;
  nir: string;
  mutual?: File | null;
  nirAlternative?: File;
  ticketModerateur?: File;
  phone?: string;
  referring_doctor?: string;
  address_line_1: string;
  address_line_2?: string;
  postcode: string;
  city: string;
  country: string;
  sidep?: Object;
  alreadyClient: boolean;
  consent_use_of_medical_data: boolean;
  isRopc: boolean;
}

export default class AuthenticationRepository extends BaseRepository<AuthenticationParams> {
  static params = {
    clientId: process.env.REACT_APP_ROPC_URL_PARAM_CLIENT_ID,
    logoutUrl: process.env.REACT_APP_MSAL_POST_LOGOUT_REDIRECT_URI,
    baseSSOUrl: process.env.REACT_APP_ROPC_BASE_URL
  };

  static login(
    credentials: AuthenticationParams
  ): Promise<{ user: User; accessToken: string; isRopc: boolean }> {
    const requestPatientOptions = {
      method: 'GET',
      headers: { Authorization: `Bearer ${credentials.accessToken}` }
    };
    return fetch(`${this.apiUrl}/me`, requestPatientOptions)
      .then(this.handleResponse)
      .then(({ data: { user, patient, access_token } }) => {
        return {
          user: User.createFromEntity({
            patient,
            ...user
          }),
          accessToken: access_token,
          isRopc: false
        };
      });
  }

  static register(credentials: RegistrationParams): Promise<UserState> {
    const entity = {
      email: credentials.email || '',
      password: credentials.password || '',
      name: credentials.name || '',
      firstname: credentials.firstName || '',
      consent_use_of_medical_data: +!!credentials.consent_use_of_medical_data,
      patient: {
        name: credentials.name,
        firstname: credentials.firstName || '',
        birthname: credentials.birthName || '',
        gender: credentials.gender,
        birthdate: credentials.birthdate,
        nir: credentials.nir,
        mutual: credentials.mutual,
        nir_alternative: credentials.nirAlternative,
        ticket_moderateur: credentials.ticketModerateur,
        phone: credentials.phone,
        referring_doctor: credentials.referring_doctor,
        address: {
          line_1: credentials.address_line_1,
          postcode: credentials.postcode,
          city: credentials.city,
          country: credentials.country || 'fr'
        },
        new_patient: +!credentials.alreadyClient,
        sidep: credentials.sidep ? JSON.stringify(credentials.sidep) : undefined
      }
    };

    let formData = createFormData(entity);

    const requestOptions = {
      method: 'POST',
      body: formData
    };

    let response = {
      patient: {},
      user: {}
    };

    return fetch(`${this.apiUrl}/register`, requestOptions)
      .then(this.handleResponse)
      .then(({ data: { user, patient } }) => {
        response = { user, patient };
      })
      .then(() => this.getADB2CToken(entity.email, entity.password))
      .then(({ id_token }) => {
        return {
          user: User.createFromEntity({
            patient: { ...response.patient },
            ...response.user
          }),
          accessToken: id_token,
          isRopc: credentials.isRopc
        };
      });
  }

  static getADB2CToken(
    email: string,
    password: string
  ): Promise<{ id_token: string }> {
    if (!this.params.clientId) {
      return Promise.reject();
    }
    const object = {
      username: email,
      password: password,
      grant_type: 'password',
      scope: `openid offline_access ${this.params.clientId}`,
      client_id: this.params.clientId,
      response_type: 'id_token token'
    };
    const formUrlEncoded = new URLSearchParams(
      Object.entries(object)
    ).toString();

    const reqOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: formUrlEncoded
    };

    return fetch(
      `${this.params.baseSSOUrl}/B2C_1A_ROPC_Auth/oauth2/v2.0/token`,
      reqOptions
    ).then(this.handleADB2CResponse);
  }

  static deleteAccount(password: string): Promise<void> {
    const requestOptions = {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', ...this.authHeader() },
      body: JSON.stringify({
        password
      })
    };

    return fetch(`${this.apiUrl}/user`, requestOptions)
      .then((res) => this.handleResponse(res, false))
      .then(() => {
        EventBus.success('Votre compte a bien été supprimé.');
        return Promise.resolve();
      })
      .catch(() => {
        EventBus.success('Mot de passe incorrect.');
        return Promise.reject();
      });
  }

  static handleResponse(response: Response, unauthenticate = true) {
    return response.text().then((text: string) => {
      const data = text && JSON.parse(text);
      if (!response.ok) {
        let error = (data && data.message) || response.statusText;
        let errorString = '';

        if (data.errors) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          for (let [key, value] of Object.entries(data.errors)) {
            errorString += `${value}<br/><br/>`;
          }
        }

        if (unauthenticate) {
          handleResponseError(response, data, errorString || error);
        }

        return Promise.reject(error);
      }

      return data;
    });
  }

  static handleADB2CResponse(response: Response, unauthenticate = true) {
    return response.text().then((text: string) => {
      const data = text && JSON.parse(text);

      if (!response.ok) {
        let error = data && data.error;
        let errorString = '';

        if (error === 'access_denied') {
          errorString += `Email ou mot de passe incorrect.`;
        }

        if (unauthenticate) {
          handleResponseError(response, data, errorString || error);
        }

        return Promise.reject(error);
      }

      return data;
    });
  }

  static logout() {
    store.dispatch(logout);
  }
}
